import { format } from 'date-fns';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export interface RequestDepositConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The policy amount, already formatted (e.g. `€50.00`). */
  amount: string | null;
  /** When the link would lapse (ISO string). */
  projectedExpiresAt: string;
  /** False when there is no usable email on file — Checkout asks for one. */
  receiptEmailAvailable: boolean;
  /** Create the link. The dialog closes itself on activation. */
  onConfirm: () => void;
}

/**
 * The review step before staff create a deposit payment link (ENG-1306).
 *
 * Kept apart from `DepositLinkActions` so the confirm-delete-dialog gate can
 * exempt exactly this dialog: it CREATES a Stripe Checkout link, nothing is
 * removed. The destructive revoke beside it renders `ConfirmDeleteDialog`.
 */
export function RequestDepositConfirmDialog({
  open,
  onOpenChange,
  amount,
  projectedExpiresAt,
  receiptEmailAvailable,
  onConfirm,
}: RequestDepositConfirmDialogProps) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Request a {amount} deposit?</AlertDialogTitle>
          <AlertDialogDescription>
            The amount comes from this service's deposit policy and is paid to
            your clinic's Stripe account. The link expires{' '}
            {format(new Date(projectedExpiresAt), 'EEE d MMM HH:mm')}. The
            booking is confirmed once the payment goes through; if it isn't paid
            by then, the booking is kept and you can send a new link.
            {!receiptEmailAvailable &&
              ' The patient has no email on file that can receive a receipt, so Stripe will ask for one at checkout and send the receipt there.'}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm}>Create link</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
